import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroupDirective,
    NgForm,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from '@angular/forms';
import { RegionEntityService } from '../../service/region-entity.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject, Subscription, buffer, debounceTime, distinctUntilChanged, filter, of, switchMap, take, takeUntil } from 'rxjs';
import { Region } from 'src/app/model/region.model';
import { HelperService } from 'src/app/service/helper.service';
import { LoginTokenService } from 'src/app/service/login-token.service';

@Component({
    selector: 'region-dropdown-two-way',
    templateUrl: './region-dropdown-two-way.component.html',
    styleUrl: './region-dropdown-two-way.component.scss'
})
export class RegionDropdownTwoWayComponent implements OnInit {
    @Input() RegionCode: string;
    @Input() isMultiple: boolean = true;
    @Input() allowAllRegions: boolean = false;
    @Input() disableAutoComplete: boolean = false;
    @Input() ErrorStateMatcher: any = null;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input('entitlementData') entitlementData: boolean = false;
    @Input('disabled') disabled: boolean;
    @Input('selectUserRegion') selectUserRegion: boolean = false;
    @Input('customLabel') customLabel: string;

    @Input() selectedRegions: Region[] = [];
    @Output() selectedRegionsChange = new EventEmitter<Region[]>();

    @Input() selectedRegion: Region;
    @Output() selectedRegionChange = new EventEmitter<Region>();
    @Output() loadingChange = new EventEmitter<boolean>();

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    fieldLabel: string;

    regionForm: UntypedFormGroup;
    regionIds: number[] = [];
    selectedRegionIds: number[] = [];

    selectedRegionName: string | undefined;
    filteredRegions: Region[] = [];
    filteredRegionsShow: Region[] = [];

    regions: Region[] = [];
    allRegions: Region[] = [];

    userRegion: Region = new Region();
    userRegionLoaded: boolean = false;
    hasAdminRole: boolean = false;

    emitTimeout: any;
    timeout: any;
    loaded: boolean = false;
    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 100;

    burstMS: number = 100;
    private changes$ = new Subject<any | null>();
    private destroy$ = new Subject<void>();
    private lastEmitted: any = undefined;
    private hasInitialized = false;

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }

    constructor(
        private fb: FormBuilder,
        private regionService: RegionEntityService,
        private helperService: HelperService,
        private loginTokenService: LoginTokenService
    ) {
        this.regionForm = this.fb.group({
            regionSelect: [''],
            filter: ['']
        });

        this.loaded$ = regionService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded || this.disabled) {
                this.regionSelect.disable();
            } else {
                this.regionSelect.enable();
            }
        });
        this.subs.push(loadedSub);

        const burstEnd$ = this.changes$.pipe(debounceTime(this.burstMS));

        this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((values) => {
            if (!values.length) return;

            // Pick the last non-empty user-driven value
            const preferred = [...values].reverse().find((v) => this.isNonEmpty(v));
            if (!preferred) return;

            this.emitNormalized(preferred);
        });
    }

    private isNonEmpty(value: any): boolean {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    }

    private emitNormalized(value: any) {
        let normalized: number[];

        if (Array.isArray(value)) {
            normalized = value.map((r: any) => r.ID);
        } else if (value && typeof value === 'object') {
            normalized = [value.ID]; // ✅ ALWAYS ARRAY
        } else if (value != null) {
            normalized = [value];
        } else {
            normalized = [];
        }

        if (this.isSame(normalized, this.lastEmitted)) return;

        this.lastEmitted = normalized;
        this.emit(normalized);
    }

    private isSame(a: any, b: any): boolean {
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }

        return a === b;
    }

    subscribers() {
        // let regionEntitiesSub = this.regionService.entities$.subscribe((regions: Region[]) => {
        //     if (regions && regions.length > 0) {
        //         this.allRegions = regions;
        //         this.regionIds = this.allRegions.length > 0 ? this.allRegions.map((region) => region.ID!) : [];
        //         this.changed = true;

        //         this.loginTokenService.isLoggedIn().subscribe((isLoggedIn) => {
        //             if (isLoggedIn) {
        //                 let lstUserProfileSub = this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
        //                     if (userRegion && userRegion.length > 0) {
        //                         ////console.warn('userRegion', userRegion);
        //                         this.userRegion = userRegion[0];
        //                         this.initUser();
        //                         this.loaded = true;
        //                         this.refreshDataRequest();
        //                     }
        //                 });
        //                 this.subs.push(lstUserProfileSub);
        //             } else {
        //                 this.loaded = true;
        //                 this.refreshDataRequest();
        //             }
        //         });
        //     }
        // });
        let regionEntitiesSub = this.regionService.entities$
            .pipe(
                filter((regions) => !!regions?.length),
                switchMap((regions) => {
                    this.allRegions = regions;
                    this.regionIds = regions.map((r) => r.ID!);
                    this.changed = true;

                    return this.loginTokenService.isLoggedIn();
                }),
                switchMap((isLoggedIn) => {
                    if (isLoggedIn) {
                        return this.helperService.lstUserProfileRegion$.pipe(filter((regions) => !!regions && regions.length > 0));
                    }
                    return of(null);
                })
            )
            .subscribe((userRegion: Region[] | null) => {
                if (userRegion?.length) {
                    this.userRegion = userRegion[0];
                    this.initUser();
                }

                this.loaded = true;
                this.refreshDataRequest();
            });
        this.subs.push(regionEntitiesSub);

        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredRegions = this.regions;
            } else {
                this.filteredRegions = this.regions.filter(
                    (region) => region.CODE!.toLowerCase().includes(value.toLowerCase()) || region.NAME!.toLowerCase().includes(value.toLowerCase())
                );
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(regionIds: number[] | number) {
        const ids = Array.isArray(regionIds) ? regionIds : [regionIds];

        // BEFORE INITIAL LOAD: block empty emits
        if (!this.hasInitialized && (!ids || ids.length === 0)) {
            return;
        }

        // If data not ready yet, store intent and exit
        if (!this.regions.length) {
            this.selectedRegionIds = ids;
            return;
        }

        // Replace (NOT merge)
        this.selectedRegionIds = [...ids];

        let output: Region[] = this.regions.filter((r) => ids.includes(r.ID));

        this.selectedRegionName = output.map((r) => r.CODE).join(', ');

        this.filterInput?.patchValue('');

        output[0] ? this.goToPage(output[0].ID!) : this.goToPage(0);

        // Always emit array
        this.selectedRegionsChange.emit(output);

        // AFTER INIT: allow empty
        this.selectedRegionChange.emit(output.length ? output[0] : undefined);
    }

    updateSelectionView() {}

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'Region';
        }

        this.subscribers();
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Handle reset logic
        if (changes['reset'] && changes['reset'].currentValue) {
            if (this.selectUserRegion) {
                this.selectedRegionIds = [this.userRegion.ID];
            } else if (this.hasAdminRole || this.allowAllRegions) {
                this.deSelectAll();
            } else {
                this.selectedRegionIds = [this.userRegion.ID];
            }
            this.changed = true;
            this.refreshDataRequest();
            this.reset = false;
        }

        // Enable/disable regionSelect based on roles
        if ((this.hasAdminRole || this.allowAllRegions) && !this.disabled) {
            this.regionSelect.enable();
        } else {
            this.regionSelect.disable();
        }

        // Only emit new values if they are non-empty
        if (changes['selectedRegions'] && this.isNonEmpty(changes['selectedRegions'].currentValue)) {
            this.changes$.next(changes['selectedRegions'].currentValue);
        }

        if (changes['selectedRegion'] && this.isNonEmpty(changes['selectedRegion'].currentValue)) {
            const region = changes['selectedRegion'].currentValue;

            if (changes['selectedRegion'].firstChange) {
                // Initial @Input: select and emit immediately
                this.selectedRegionIds = [region.ID];
                this.regionSelect.patchValue(region.ID);
                //this.emitSelect([region.ID]); // <-- ensures selectedRegionChange fires

                if (this.loaded) {
                    this.emitSelect([region.ID]);
                }
            } else {
                // Subsequent user-driven changes go through debounce/buffer
                this.changes$.next(region);
            }
        }
    }

    emit(ids: any) {
        const normalizedIds = Array.isArray(ids) ? ids : [ids];

        // Preserve selection even if data not ready
        this.selectedRegionIds = normalizedIds;

        if (!this.regions.length) return;

        this.select(normalizedIds);
        this.emitSelect(normalizedIds);
    }

    initUser() {
        let user = this.loginTokenService.getUser();

        let adminUserIds = [1, 9]; // System Administrator (1), State Administrator (9)

        this.hasAdminRole = user.USER_ROLES?.some((ur) => adminUserIds.includes(ur.ROLE?.ID ? ur.ROLE.ID : 0))!;

        if (this.hasAdminRole || this.allowAllRegions) {
            this.regionSelect.enable();
        } else {
            this.regionSelect.disable();
            ////console.warn('1 - S - setting user region - ' + this.userRegion.CODE);
            this.selectedRegionIds = [this.userRegion.ID];
        }

        if (this.selectUserRegion) {
            this.selectedRegionIds = [this.userRegion.ID];
        }

        if (this.disabled) {
            this.regionSelect.disable();
        }
    }

    refreshDataRequest() {
        this.loaded$.pipe(filter(Boolean), take(1)).subscribe(() => this.loadData(true));

        // if (this.timeout) {
        //     clearTimeout(this.timeout);
        // }

        // this.timeout = setTimeout(() => {
        //     if (!this.loaded) {
        //         this.refreshDataRequest();
        //     } else {
        //         this.loadData(true);
        //     }
        // }, 100);
    }

    loadData(open: any) {
        if (open) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;
            if (!this.changed) return;

            this.regions = [];
            this.isLoading = true;
            this.loadingChange.emit(true);

            this.regionSelect.disable();

            this.changed = false;

            this.filterInput!.patchValue('');

            let workingRegions = this.allRegions;

            this.regions = Array.from(new Set(workingRegions));

            this.regions.sort((a, b) => (this.userRegion && a.ID === this.userRegion.ID ? -1 : a.CODE!.localeCompare(b.CODE!)));
            this.filteredRegions = this.regions;
            this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });

            ////console.warn('1 - L - filteredRegions (' + this.filteredRegions.length + '/' + this.allRegions.length + ')');
            if (this.regions.length === 1 && !this.disableAutoComplete) {
                this.regionSelect.patchValue([]);
                this.selectAll();
                this.emitSelect(this.selectedRegionIds);
            } else {
                this.select(this.selectedRegionIds);
                this.emitSelect(this.selectedRegionIds);
            }

            if (!this.disabled) {
                this.regionSelect.enable();
            }

            if (!this.hasInitialized) {
                this.hasInitialized = true;
            }

            this.isLoading = false;
            this.loadingChange.emit(false);
        } else {
            this.select(this.selectedRegionIds);
            this.emitSelect(this.selectedRegionIds);
        }
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));
        return arr;
    }

    select(selection: any) {
        this.filteredRegions = this.regions;

        if (this.isMultiple) {
            // Remove all selections in current page
            let currentPageIds = this.filteredRegionsShow.map((show) => show.ID);
            this.selectedRegionIds = this.selectedRegionIds.filter((selectedRegionId) => !currentPageIds.includes(selectedRegionId));

            // Add current selection to total array
            this.selectedRegionIds = Array.from(new Set(this.selectedRegionIds.concat(selection)));

            // Remove all IDs that are not in available regions
            let filteredRegions = this.filteredRegions.map((filteredRegion) => filteredRegion.ID);
            this.selectedRegionIds = this.selectedRegionIds.filter((selectedRegionId) => filteredRegions.includes(selectedRegionId));

            this.regionSelect.patchValue(this.selectedRegionIds);
        } else {
            if (Array.isArray(selection)) {
                this.regionSelect.patchValue(selection[0]);
                this.selectedRegionIds = selection;
            } else {
                this.regionSelect.patchValue(selection);
                this.selectedRegionIds = [selection];
            }

            const ids = Array.isArray(this.selectedRegionIds) ? this.selectedRegionIds : [this.selectedRegionIds];
            //this.emitSelect(ids);
            this.goToPage(this.regionSelect.value);
        }
    }

    selectAll() {
        let selected = this.regionSelect.value; // Currently selected
        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            this.selectedRegionIds = Array.from(new Set(selected.concat(this.filteredRegions.map((region) => region.ID!)))); // Select all filtered (if any filter)
            this.regionSelect.patchValue(this.selectedRegionIds);
        } else {
            this.selectedRegionIds = this.filteredRegions.map((region) => region.ID!);
            this.regionSelect.patchValue(this.selectedRegionIds[0]);
        }
    }

    deSelectAll() {
        //this.reset = false;
        this.selectedRegionName = '';
        this.regionSelect.patchValue([]);
        this.selectedRegionIds = [];
        this.filterInput?.patchValue('');
        //this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get regionSelect(): any {
        return this.regionForm.get('regionSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.regionForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredRegionsShow = this.filteredRegions.slice($event.pageIndex * $event.pageSize, $event.pageIndex * $event.pageSize + $event.pageSize);
    }

    goToPage(id: number) {
        let index = this.filteredRegions.findIndex((filteredRegion) => filteredRegion.ID === id);
        let pageIndex = Math.floor(index / this.pageSize);

        if (this.paginator) {
            this.paginator.pageIndex = pageIndex;
        }
        this.onPageChange({ pageIndex: pageIndex, pageSize: this.pageSize });
    }
}
